const rulebookSchema = {
  "@context": "https://schema.org",
  "@graph": [
    // HOWTO: main "How to play" guide
    {
      "@type": "HowTo",
      "@id": "https://yutnori.app/how-to-play#howto",
      "name": "How to Play Yutnori (윷놀이)",
      "description":
        "Step-by-step guide to playing Yutnori (윷놀이), a traditional Korean board game, with simple explanations and examples.",
      "image": "https://yutnori.app/og-image.png",
      "totalTime": "PT10M",
      "inLanguage": ["ko", "en"],
      "supply": [
        { "@type": "HowToSupply", "name": "4 yut sticks (윷)" },
        { "@type": "HowToSupply", "name": "Yut board" },
        { "@type": "HowToSupply", "name": "Game pieces for each team" }
      ],
      "step": [
        {
          "@type": "HowToStep",
          "name": "Gather what you need",
          "text":
            "Prepare four yut sticks, a circular Yut board, and pieces for two teams. Each team usually has four pieces.",
          "url": "https://yutnori.app/how-to-play#what-you-need"
        },
        {
          "@type": "HowToStep",
          "name": "Understand how the sticks work",
          "text":
            "Throw the four sticks. The way they land determines how many spaces you move: Do (1), Gae (2), Geol (3), Yut (4 + extra throw), Mo (5 + extra throw), Backdo (-1), and Nak (0).",
          "url": "https://yutnori.app/how-to-play#sticks"
        },
        {
          "@type": "HowToStep",
          "name": "Move your pieces along the path",
          "text":
            "Move one of your pieces forward according to your throw. Follow the path around the board and use diagonal shortcuts at corners when possible.",
          "url": "https://yutnori.app/how-to-play#move"
        },
        {
          "@type": "HowToStep",
          "name": "Stack your own pieces",
          "text":
            "If two of your pieces land on the same spot, you can stack them. Stacked pieces move together as one, making them faster but riskier.",
          "url": "https://yutnori.app/how-to-play#stacking"
        },
        {
          "@type": "HowToStep",
          "name": "Capture opponent pieces",
          "text":
            "If you land on an opponent's piece, you capture it, send it back to start, and you get another turn.",
          "url": "https://yutnori.app/how-to-play#capturing"
        },
        {
          "@type": "HowToStep",
          "name": "Complete the course to win",
          "text":
            "Be the first team to move all of your pieces around the board and off the finish point.",
          "url": "https://yutnori.app/how-to-play#win"
        },
        {
          "@type": "HowToStep",
          "name": "See a simple example turn",
          "text":
            "Example: You throw Geol (3), move one piece three spaces, and if you land on an enemy piece you send it back and get another throw. Yut and Mo also grant an extra throw.",
          "url": "https://yutnori.app/how-to-play#example-turn"
        },
        {
          "@type": "HowToStep",
          "name": "Use beginner tips",
          "text":
            "Try to capture for extra turns, use the diagonal shortcuts, stack carefully, and remember that Backdo can sometimes bring a token directly to the finish.",
          "url": "https://yutnori.app/how-to-play#tips"
        }
      ]
    },

    // ARTICLE: treat the rulebook as an article / official writeup
    {
      "@type": "Article",
      "@id": "https://yutnori.app/how-to-play#article",
      "headline": "How to Play Yutnori (윷놀이) – Full Rulebook",
      "description":
        "A clear, beginner-friendly rulebook for Yutnori (윷놀이), explaining the throws, movement, stacking, capturing, and how to win, plus strategy tips.",
      "image": "https://yutnori.app/og-image.png",
      "author": {
        "@type": "Person",
        "name": "Beat Rhino"
      },
      "publisher": {
        "@type": "Organization",
        "name": "yutnori.app",
        "logo": {
          "@type": "ImageObject",
          "url": "https://yutnori.app/logo.png"
        }
      },
      "datePublished": "2025-01-01",
      "mainEntityOfPage": "https://yutnori.app/how-to-play"
    },

    // GAME: say this page is about the Yutnori game
    {
      "@type": "Game",
      "@id": "https://yutnori.app/how-to-play#game",
      "name": "Yutnori (윷놀이)",
      "description":
        "Yutnori is a traditional Korean board game using four wooden sticks. This rulebook explains all the official rules and basic strategies.",
      "url": "https://yutnori.app/how-to-play",
      "genre": ["Board game", "Strategy", "Korean Traditional Game"],
      "inLanguage": ["ko", "en"],
      "image": "https://yutnori.app/og-image.png",
      "publisher": {
        "@type": "Organization",
        "name": "yutnori.app"
      }
    },

    // BREADCRUMB: navigation structure
    {
      "@type": "BreadcrumbList",
      "@id": "https://yutnori.app/how-to-play#breadcrumb",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://yutnori.app/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "How to Play",
          "item": "https://yutnori.app/how-to-play"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "About",
          "item": "https://yutnori.app/about"
        }
      ]
    },

    // FAQ: a few common questions that map to your text
    {
      "@type": "FAQPage",
      "@id": "https://yutnori.app/how-to-play#faq",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Yutnori?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Yutnori is a traditional Korean board game played with four wooden sticks called yut. Players move pieces around a board based on how the sticks land, trying to move all their pieces off the board first."
          }
        },
        {
          "@type": "Question",
          "name": "What do I need to play Yutnori?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "To play Yutnori, you need four yut sticks, a Yut board with a circular path, and pieces for two teams. You can also play online using the digital version at yutnori.app."
          }
        },
        {
          "@type": "Question",
          "name": "How do the Yutnori stick throws work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Each throw of the four sticks corresponds to a movement: Do (1 space), Gae (2), Geol (3), Yut (4 and extra throw), Mo (5 and extra throw), Backdo (-1 backward with a marked stick), and Nak (0, no movement when the sticks go out of bounds)."
          }
        },
        {
          "@type": "Question",
          "name": "How do you win a game of Yutnori?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "You win Yutnori by moving all of your pieces around the board and off the finish point before the other team. Capturing opponent pieces and using shortcuts are key strategies."
          }
        },
        {
          "@type": "Question",
          "name": "Can I play Yutnori online?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Yes. You can play a 3D animated online version of Yutnori with a built-in rulebook at https://www.yutnori.app."
          }
        }
      ]
    }
  ]
};

export default rulebookSchema;
